import { useState, useRef, useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

export default function PaginationButtons(props: {
  page: number;
  setPage: (page: number) => void;
  pageCount?: number;
  pageSize?: number;
  setPageSize?: (size: number) => void;
}) {
  const { page, setPage, pageCount, pageSize, setPageSize } = props;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [positionAbove, setPositionAbove] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (dropdownOpen && dropdownRef.current && buttonRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;

      setPositionAbove(spaceBelow < dropdownRect.height);
    }
  }, [dropdownOpen]);

  const handlePageChange = (newPage: number) => () => {
    if (!pageCount) return;
    if (newPage >= 1 && newPage <= pageCount) setPage(newPage);
  };

  const handleEntriesChange = (ent: number) => {
    setPage(1);
    setPageSize?.(ent);
    setDropdownOpen(false);
  };

  const renderPageButton = (pageNum: number) => (
    <button
      key={pageNum}
      type="button"
      onClick={handlePageChange(pageNum)}
      className={`btn btn-sm min-w-10 ${
        pageNum === page
          ? "btn-primary text-white"
          : "bg-base-200 text-base-content/70 hover:bg-base-300"
      }`}
    >
      {pageNum}
    </button>
  );

  if (!pageCount) return null;

  return (
    <div className="flex justify-between items-center flex-wrap gap-4 px-1 mt-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handlePageChange(page - 1)}
          className="btn btn-sm bg-base-200 hover:bg-base-300 text-base-content/70"
          disabled={page <= 1}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>

        {pageCount > 7 ? (
          <>
            {renderPageButton(1)}
            {page > 4 && (
              <div className="text-sm text-base-content/50 px-1">...</div>
            )}
            {Array.from({ length: pageCount }, (_, i) => i + 1)
              .filter(
                (n) =>
                  n !== 1 &&
                  n !== pageCount &&
                  ((page < 5 && n <= 5) ||
                    (page > pageCount - 4 && n >= pageCount - 4) ||
                    Math.abs(n - page) <= 1)
              )
              .map(renderPageButton)}
            {page < pageCount - 3 && (
              <div className="text-sm text-base-content/50 px-1">...</div>
            )}
            {renderPageButton(pageCount)}
          </>
        ) : (
          <>
            {Array.from({ length: pageCount }, (_, i) =>
              renderPageButton(i + 1)
            )}
          </>
        )}

        <button
          type="button"
          onClick={handlePageChange(page + 1)}
          className="btn btn-sm bg-base-200 hover:bg-base-300 text-base-content/70"
          disabled={page >= pageCount}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>

      {pageSize && setPageSize && (
        <div className="relative">
          <button
            ref={buttonRef}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="btn btn-sm bg-base-200 hover:bg-base-300 text-sm"
          >
            {pageSize} / page
            <svg
              className="ml-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {dropdownOpen && (
            <div
              ref={dropdownRef}
              className={`absolute z-10 w-28 rounded-lg shadow bg-base-100 border border-base-300 ${
                positionAbove ? "bottom-full mb-2" : "mt-1"
              }`}
            >
              <div className="flex flex-col">
                {[10, 25, 50].map((entry) => (
                  <button
                    key={entry}
                    className={`px-4 py-2 text-sm text-left hover:bg-base-200 ${
                      pageSize === entry ? "text-primary font-medium" : ""
                    }`}
                    onClick={() => handleEntriesChange(entry)}
                  >
                    {entry} / page
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
